import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
// A stalled variant request must leave a visible recovery path without
// re-enabling purchase against the previously selected variant.
const VARIANT_UPDATE_WAIT_DURATION = 10000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick() {
    if (!this.closest('form')?.checkValidity()) return;
    this.#preloadImage();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;
    if (addToCartButton.dataset.puppet === 'true') return;
    if (this.dataset.addToCartAnimation === 'true' && !this.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  #addToCartInProgress = false;
  #variantAvailable = true;

  #variantWaitMessageShown = false;
  #variantWaitTimeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#clearVariantWait();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    // Properties and selling plans can split one variant across multiple lines.
    const cartQty = cart.items.reduce((total, item) =>
      item.variant_id.toString() === variantIdInput.value.toString() ? total + item.quantity : total, 0);

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch(`${Theme.routes.cart_url || '/cart'}.js`);
      if (!response.ok) throw new Error('Cart refresh failed');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#addToCartInProgress) return;

    if (this.#variantChangeInProgress) {
      this.#showVariantUpdatingError();
      return;
    }

    return this.#processAddToCart(event);
  }

  #showVariantUpdatingError(message = this.dataset.variantUpdating || 'Please wait for your selection to finish updating, then add it to your cart.') {
    this.#variantWaitMessageShown = true;
    return this.#showCartError({ message }, false);
  }

  #clearVariantWait() {
    clearTimeout(this.#variantWaitTimeout);
    this.#variantWaitTimeout = undefined;
    if (this.#variantWaitMessageShown) {
      this.#variantWaitMessageShown = false;
      this.refs.addToCartTextError?.classList.add('hidden');
      this.#clearLiveRegionText();
    }
  }

  /**
   * @param {Array<string | number>} addOnIds
   * @param {Event} [event]
   */
  async addWithAddOns(addOnIds = [], event) {
    if (this.#variantChangeInProgress) {
      return { success: false, error: this.#showVariantUpdatingError() };
    }
    return this.#processAddToCart(event, addOnIds);
  }

  #showCartError(response = {}, reviewCart = true) {
    const fallback = this.dataset.cartError || 'Error adding to cart';
    const detail = typeof response.description === 'string' ? response.description : response.message;
    const message = [typeof detail === 'string' && detail ? detail : fallback, reviewCart && this.dataset.cartReviewError]
      .filter(Boolean).join(' ');
    const { addToCartTextError } = this.refs;
    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');
      let text = addToCartTextError.querySelector('[data-cart-error-message]');
      if (!text) {
        text = document.createElement('span');
        text.setAttribute('data-cart-error-message', '');
        addToCartTextError.appendChild(text);
      }
      text.textContent = message;
    }
    this.#setLiveRegionText(message);
    this.dispatchEvent(new CartErrorEvent(this.id, message, response.description, response.errors));
    return message;
  }

  /**
   * @param {Event} [event]
   * @param {Array<string | number>} [addOnIds]
   */
  async #processAddToCart(event, addOnIds = []) {
    if (this.#addToCartInProgress) return { success: false, error: '' };
    const { addToCartTextError } = this.refs;
    if (this.#timeout) clearTimeout(this.#timeout);
    const allAddToCartContainers = Array.from(this.querySelectorAll('add-to-cart-component'));
    if (allAddToCartContainers.some((container) => container.refs.addToCartButton?.disabled)) {
      return { success: false, error: this.#showCartError() };
    }
    const form = this.querySelector('form');
    if (!form || !form.reportValidity()) return { success: false, error: this.#showCartError() };

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        const message = (this.dataset.quantityErrorMax || '').replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        return { success: false, error: this.#showCartError({ message }) };
      }
    }

    const formData = new FormData(form);
    const id = formData.get('id');
    const submittedProductId = this.dataset.productId;
    const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const extras = [...new Set(addOnIds.map(String))];
    if (!id || !/^\d+$/.test(String(id)) || !Number.isInteger(quantity) || quantity < 1 || extras.some((extra) => !/^\d+$/.test(extra) || Number(extra) <= 0)) {
      return { success: false, error: this.#showCartError() };
    }
    formData.set('quantity', String(quantity));
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item.dataset.sectionId).filter(Boolean);
    if (sectionIds.length) formData.set('sections', [...new Set(sectionIds)].join(','));

    let requestData = formData;
    if (extras.length) {
      requestData = new FormData();
      for (const [name, value] of formData) {
        const property = name.match(/^properties\[(.*)\]$/);
        if (property) requestData.append(`items[0][properties][${property[1]}]`, value);
        else if (['id', 'quantity', 'selling_plan'].includes(name)) requestData.append(`items[0][${name}]`, value);
        else requestData.append(name, value);
      }
      extras.forEach((extra, index) => {
        requestData.append(`items[${index + 1}][id]`, extra);
        requestData.append(`items[${index + 1}][quantity]`, String(quantity));
      });
    }

    this.#addToCartInProgress = true;
    this.setAttribute('aria-busy', 'true');
    allAddToCartContainers.forEach((container) => container.disable());
    const fetchCfg = fetchConfig('javascript', { body: requestData });
    try {
      const httpResponse = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'application/json' },
      });
      const response = await httpResponse.json();
      if (!httpResponse.ok || response.status) {
        throw { cartResponse: response };
      }
      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }
      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      const addedText = anyAddToCartButton?.querySelector('.add-to-cart-text--added')?.textContent?.trim() || Theme.translations.added;
      this.#setLiveRegionText(addedText);
      // A shopper can choose another variant while this request is pending.
      // Confirm the submitted item without marking the new selection as added.
      if (this.refs.variantId?.value === String(id) && !this.#variantChangeInProgress) {
        this.querySelectorAll('add-to-cart-component').forEach((container) => container.animateAddToCart?.());
      }
      this.#timeout = setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
      await this.#fetchAndUpdateCartQuantity();
      this.dispatchEvent(new CartAddEvent({}, id.toString(), {
        source: 'product-form-component',
        itemCount: quantity * (extras.length + 1),
        productId: submittedProductId,
        sections: response.sections,
      }));
      return { success: true };
    } catch (error) {
      const message = this.#showCartError(error?.cartResponse);
      await this.#fetchAndUpdateCartQuantity();
      this.dispatchEvent(new CartAddEvent({}, this.id, {
        didError: true,
        source: 'product-form-component',
        itemCount: quantity,
        productId: submittedProductId,
      }));
      return { success: false, error: message };
    } finally {
      this.#addToCartInProgress = false;
      this.removeAttribute('aria-busy');
      this.querySelectorAll('add-to-cart-component').forEach((container) => {
        if (this.#variantAvailable && !this.#variantChangeInProgress) container.enable();
        else container.disable();
      });
      if (event) cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;
    this.#variantAvailable = event.detail.resource != null && event.detail.resource.available !== false;

    this.#clearVariantWait();

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else if (!this.#addToCartInProgress) {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }
    if (this.#addToCartInProgress || !this.#variantAvailable) {
      currentAddToCartButtonContainer.disable();
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    if (this.#addToCartInProgress || !this.#variantAvailable) {
      this.querySelectorAll('add-to-cart-component').forEach((container) => container.disable());
    }

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#clearVariantWait();
    this.#variantChangeInProgress = true;
    this.#variantWaitTimeout = setTimeout(() => {
      if (this.#variantChangeInProgress) {
        this.#showVariantUpdatingError(this.dataset.variantUpdateRecovery || 'Your selection could not finish loading. Choose an option again or refresh the page.');
      }
    }, VARIANT_UPDATE_WAIT_DURATION);
    // Wait for Shopify's resolved variant and quantity rules. Do not queue an
    // automatic purchase using a stale URL or incomplete form snapshot.
    this.querySelectorAll('add-to-cart-component').forEach((container) => container.disable());
    this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
